const DEFAULT_REDIRECT = '/dashboard';
const MAX_LEN = 2048;

/** Pathnames where we do not attach a return URL when sending users to login. */
const PUBLIC_AUTH_PATHNAMES = new Set([
  '/',
  '/login',
  '/register',
  '/forgot-password',
  '/invitations/accept',
  '/terms',
]);

export function isPublicAuthPathname(pathname: string): boolean {
  return PUBLIC_AUTH_PATHNAMES.has(pathname);
}

export type SanitizePostLoginRedirectOptions = {
  canAccessAdmin: boolean;
};

/**
 * Returns a safe in-app path for post-login navigation from the raw `redirect` query value.
 * Rejects protocol-relative URLs, obvious open-redirect patterns, and oversize values.
 */
export function sanitizePostLoginRedirect(
  raw: string | null,
  options: SanitizePostLoginRedirectOptions
): string {
  if (raw == null || raw === '') {
    return DEFAULT_REDIRECT;
  }

  let decoded = raw.trim();
  if (decoded.length > MAX_LEN) {
    return DEFAULT_REDIRECT;
  }

  try {
    decoded = decodeURIComponent(decoded);
  } catch {
    return DEFAULT_REDIRECT;
  }

  if (decoded.length > MAX_LEN) {
    return DEFAULT_REDIRECT;
  }

  if (!decoded.startsWith('/') || decoded.startsWith('//')) {
    return DEFAULT_REDIRECT;
  }

  if (
    decoded.includes('\\') ||
    decoded.includes('@') ||
    decoded.includes('://')
  ) {
    return DEFAULT_REDIRECT;
  }

  if (decoded.includes('..')) {
    return DEFAULT_REDIRECT;
  }

  if (decoded.includes('\0')) {
    return DEFAULT_REDIRECT;
  }

  if (isPublicAuthPathname(decoded.split('?')[0] ?? '')) {
    return DEFAULT_REDIRECT;
  }

  const pathOnly = decoded.split('?')[0] ?? '';
  if (
    !options.canAccessAdmin &&
    (pathOnly === '/admin' || pathOnly.startsWith('/admin/'))
  ) {
    return DEFAULT_REDIRECT;
  }

  return decoded;
}

export function buildLoginNavigateSearch(
  pathname: string,
  search: string
): string {
  if (isPublicAuthPathname(pathname)) {
    return '';
  }
  const returnTarget = `${pathname}${search}`;
  const params = new URLSearchParams();
  params.set('redirect', returnTarget);
  return `?${params.toString()}`;
}
